import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ReferenceLineProps,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from "recharts";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import { GraphConfig } from "@intelligems/ig-types";
import { Skeleton, Stack, useTheme } from "@mui/material";
import {
  findMaxCiRangeValue,
  findMinCiRangeValue,
  labelFormatWrapper,
} from "@src/features/analytics/metricDetails/statSig/utils";
import { theme } from "@src/theme";

import { BaseRendererProps } from "./types";

const MobileStatSigAxisTick = (props: {
  props: any;
  variantData: Array<{ value: number }>;
  config: GraphConfig;
  currency: { code: string; locale: string };
}) => {
  const { x, y, payload } = props.props;
  const shouldShowDecimals =
    Math.min(...props.variantData.map((d) => d.value)) < 0.09;

  return (
    <Text
      fill="rgba(0, 0, 0, 0.38)"
      fontSize={10}
      fontFamily={theme.typography.fontFamily}
      x={x}
      y={y - 10}
      textAnchor="middle"
      verticalAnchor="start"
    >
      {labelFormatWrapper(
        payload.value,
        props.config.dataTypes[0],
        props.currency,
        shouldShowDecimals
      )}
    </Text>
  );
};

const createHorizonBar =
  (index: number, variantCount: number) => (props: ReferenceLineProps) => {
    // props.x1/x2 are pixel coordinates, props.y1/y2 give us the chart area bounds
    const pixelX = props.x1 ?? props.x2 ?? 0;
    const y1 = (props.y1 as number) ?? 0;
    const y2 = (props.y2 as number) ?? 0;
    const chartAreaHeight = y2 - y1;
    const barHeight = chartAreaHeight / variantCount;
    const yCenter = y1 + barHeight * index + barHeight / 2;
    return (
      <line
        x1={pixelX}
        y1={yCenter - 10}
        x2={pixelX}
        y2={yCenter + 10}
        stroke={IgGrayPrimitives["A400"]}
        strokeWidth={2}
      />
    );
  };

interface ValueRendererProps extends BaseRendererProps {
  selectedConfig: GraphConfig;
}

export const ValueRenderer = ({
  variantData,
  isLoading,
  currency,
  selectedConfig,
}: ValueRendererProps) => {
  const theme = useTheme();

  const dataMax = findMaxCiRangeValue(variantData, currency);
  const dataMin = findMinCiRangeValue(variantData, currency);
  const shouldShowDecimals =
    Math.min(...variantData.map((d) => d.value)) < 0.09;

  const tickCount = 5;
  const step = (dataMax - dataMin) / (tickCount - 1);
  const customTicks = Array.from(
    { length: tickCount },
    (_, i) => +(dataMin + i * step).toFixed(4)
  );

  if (isLoading) {
    return (
      <Stack sx={{ paddingY: 2 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={variantData.length * 60}
          sx={{ borderRadius: "5px" }}
        />
      </Stack>
    );
  }

  return (
    <Stack sx={{ paddingY: 2 }}>
      <ResponsiveContainer width="100%" height={variantData.length * 60}>
        <BarChart
          layout="vertical"
          maxBarSize={20}
          data={variantData}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <XAxis
            orientation="top"
            type="number"
            dataKey="ci_range"
            ticks={customTicks}
            tickCount={5}
            domain={[dataMin, dataMax]}
            padding={{ left: 15, right: 15 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            tick={(props: any) => (
              <MobileStatSigAxisTick
                props={props}
                variantData={variantData}
                config={selectedConfig}
                currency={currency}
              />
            )}
          />
          <YAxis
            hide={true}
            tick={false}
            dataKey="variation_id"
            type="category"
            tickLine={false}
            axisLine={{ stroke: "transparent" }}
          />
          <Bar dataKey="ci_range" fill={theme.igPrimitives.gray[500]}>
            {variantData.map((variant) => (
              <Cell
                key={`cell-${variant.variation_id}`}
                fill={
                  variant.ci_range !== undefined
                    ? theme.igPrimitives.gray[500]
                    : "transparent"
                }
              />
            ))}
            <LabelList
              fontSize={12}
              dataKey="ci_range"
              position="left"
              fill={theme.igPrimitives.gray["A600"]}
              formatter={(value: number[]) =>
                value?.[0] !== undefined
                  ? labelFormatWrapper(
                      value[0],
                      selectedConfig.dataTypes[0],
                      currency,
                      shouldShowDecimals
                    )
                  : ""
              }
            />
            <LabelList
              fontSize={12}
              dataKey="ci_range"
              position="right"
              fill={theme.igPrimitives.gray["A600"]}
              formatter={(value: number[]) =>
                value?.[1] !== undefined
                  ? labelFormatWrapper(
                      value[1],
                      selectedConfig.dataTypes[0],
                      currency,
                      shouldShowDecimals
                    )
                  : ""
              }
            />
            <LabelList
              fontSize={12}
              content={(props: any) => {
                const { x, y, width, height, index } = props;
                const variant = variantData[index];
                if (variant.ci_range !== undefined) {
                  return null;
                }
                return (
                  <Text
                    x={x + width / 2}
                    y={y + height / 2}
                    fill={theme.palette.text.secondary}
                    fontSize={12}
                    textAnchor="middle"
                    verticalAnchor="middle"
                  >
                    Not Enough Data
                  </Text>
                );
              }}
            />
          </Bar>
          {variantData.map((variant, index) =>
            variant.ci_range !== undefined ? (
              <ReferenceLine
                key={variant.variation_id}
                x={variant.value}
                stroke={theme.igPrimitives.gray["A400"]}
                strokeWidth={2}
                isFront={true}
                segment={[
                  { x: variant.value, y: variant.variation_id },
                  { x: variant.value, y: variant.variation_id },
                ]}
                shape={createHorizonBar(index, variantData.length)}
              />
            ) : null
          )}
        </BarChart>
      </ResponsiveContainer>
    </Stack>
  );
};
